import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Card,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TablePagination,
  TableRow,
  Paper,
  Button,
  TableHead,
  IconButton,
  Switch,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import { Link } from "react-router-dom";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { API_ENDPOINTS } from "../../../config/api";
import { useAuth } from "../../../context/AuthContext";

const API_URL = API_ENDPOINTS.PRODUCT_TYPES;

const ProductTypesListContent = () => {
  const [productTypes, setProductTypes] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const { user } = useAuth();
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success", // 'success' | 'error'
  });
  const [deleteDialog, setDeleteDialog] = useState({
    open: false,
    id: null,
  });

  useEffect(() => {
    fetchProductTypes();
  }, []);

  const fetchProductTypes = async () => {
    try {
      const response = await axios.get(API_URL.BASE);
      const data = response.data;
      // console.log(data);
      let productTypesArray = [];
      if (Array.isArray(data)) {
        productTypesArray = data;
      } else if (data && Array.isArray(data.productTypes)) {
        productTypesArray = data.productTypes;
      }
      setProductTypes(productTypesArray);
    } catch (error) {
      console.error("Error fetching product types:", error);
      setProductTypes([]);
    }
  };

  const handleSnackbarClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const handleDeleteClick = (id) => {
    setDeleteDialog({ open: true, id });
  };

  const handleDeleteConfirm = async () => {
    const id = deleteDialog.id;
    setDeleteDialog({ open: false, id: null });
    try {
      await axios.delete(API_URL.DELETE(id));
      setProductTypes((prevProductTypes) => prevProductTypes.filter((productType) => productType.id !== id));
      setSnackbar({
        open: true,
        message: "Product type deleted successfully!",
        severity: "success",
      });
    } catch (error) {
      let userMessage = "Failed to delete product type.";
      if (
        error.response?.data?.message &&
        error.response.data.message.includes("foreign key constraint fails")
      ) {
        userMessage =
          "Cannot delete this product type because it is assigned to other records. Please reassign or remove those records first.";
      }
      setSnackbar({
        open: true,
        message: userMessage,
        severity: "error",
      });
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialog({ open: false, id: null });
  };
  

  const handleTogglePublish = async (id, currentStatus) => {
    try {
      await axios.put(API_URL.PUBLISH(id), { publish: !currentStatus });
      setProductTypes((prevProductTypes) =>
        prevProductTypes.map((productType) =>
          productType.id === id ? { ...productType, publish: !currentStatus } : productType
        )
      );
    } catch (error) {
      console.error("Error updating publish status:", error);
    }
  };

  const handleTogglePieces = async (id, currentStatus) => {
    try {
      await axios.put(API_URL.PIECES(id), { pieces: !currentStatus });
      setProductTypes((prevProductTypes) =>
        prevProductTypes.map((productType) =>
          productType.id === id ? { ...productType, pieces: !currentStatus } : productType
        )
      );
    } catch (error) {
      console.error("Error updating pieces status:", error);
    }
  };
  

  const filteredProductTypes = Array.isArray(productTypes)
    ? productTypes.filter((productType) =>
        productType.name.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : [];

  return (
    <Card sx={{ boxShadow: "none", borderRadius: "7px", mb: "25px", padding: "25px" }}>
      {/* <NavList /> */}
      <Box>
        <Box sx={{ display: "flex", justifyContent: "space-between", mb: "25px" }}>
          <input
            type="text"
            placeholder="Search Product type..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{ padding: "8px", width: "200px", borderRadius: "5px", border: "1px solid #ccc" }}
          />
          {user.permissions.includes("add-productTypes") && (
            <Link to="/ecommerce/create-productTypes">
              <Button variant="outlined" color="primary">
                <AddIcon /> Add New Product Type
              </Button>
            </Link>
          )}
        </Box>

        <TableContainer className="custom-table" component={Paper}>
          <Table sx={{ minWidth: 1000 }} aria-label="User Types Table">
            <TableHead>
              <TableRow>
                <TableCell>S.No</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Publish</TableCell>
                <TableCell>Pieces</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredProductTypes
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((productType, index) => (
                  <TableRow key={productType.id}>
                    <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                    <TableCell>{productType.name}</TableCell>
                    <TableCell>
                      <Switch
                        checked={!!productType.publish}
                        onChange={() => handleTogglePublish(productType.id, productType.publish)}
                        color="success"
                      />
                    </TableCell>
                    <TableCell>
                      <Switch
                        checked={!!productType.pieces}
                        onChange={() => handleTogglePieces(productType.id, productType.pieces)}
                        color="success"
                      />
                    </TableCell>
                    <TableCell>
                      {/* {user.permissions.includes("view-productTypes") && (
                        <Link to={`/ecommerce/productTypes-list/details/${productType.id}`}>
                          <IconButton color="info">
                            <VisibilityIcon />
                          </IconButton>
                        </Link>
                      )} */}
                      {user.permissions.includes("edit-productTypes") && (
                        <Link to={`/ecommerce/edit-productTypes/${productType.id}`}>
                          <IconButton color="primary">
                            <EditIcon />
                          </IconButton>
                        </Link>
                      )}
                      {user.permissions.includes("delete-productTypes") && (
                        <IconButton color="error" onClick={() => handleDeleteClick(productType.id)}>
                          <DeleteIcon />
                        </IconButton>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
            <TableFooter>
              <TableRow>
                <TablePagination
                  rowsPerPageOptions={[5, 10, 25, { label: "All", value: -1 }]}
                  count={filteredProductTypes.length}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={(event, newPage) => setPage(newPage)}
                  onRowsPerPageChange={(event) => {
                    setRowsPerPage(parseInt(event.target.value, 10));
                    setPage(0);
                  }}
                />
              </TableRow>
            </TableFooter>
          </Table>
        </TableContainer>
      </Box>
      <Dialog
        open={deleteDialog.open}
        onClose={handleDeleteCancel}
      >
        <DialogTitle>Delete Product Type</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this product type?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} color="primary">
            Cancel
          </Button>
          <Button onClick={handleDeleteConfirm} color="error" autoFocus>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={1000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Card>
  );
};

export default ProductTypesListContent;